import axios from 'axios';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

import { GoogleLoginSuccessCallback } from '../../../components/google-login/useGoogleLoginIFrameEventListener';
import { RecaptchaAction } from '../../../components/recaptcha/RecaptchaAction';
import { useGoogleCallback } from '../../../components/recaptcha/useGoogleCallback';
import { LoginErrorType, useLoginForm } from './LoginFormContext';
import { GoogleLoginBody, LoginResponse } from './LoginSchemas';

export function useGoogleSignInCallback(redirectAfterLogin?: string): GoogleLoginSuccessCallback {
    const router = useRouter();
    const { setLoginErrorType } = useLoginForm();

    const handleSuccess = useCallback(
        async (googleCredential: string, recaptchaToken: string | undefined) => {
            const result = await axios.post<LoginResponse>('/api/login', {
                googleCredential,
                agreementCheckbox: true,
                recaptchaToken,
                redirectAfterLogin,
            } satisfies GoogleLoginBody);
            if (result.data.success) {
                const redirectTo = result.data.redirectTo ?? '/';
                if (redirectTo.startsWith('http')) {
                    window.location.href = redirectTo;
                } else {
                    router.push(redirectTo);
                }
            } else if (result.data.reason === 'googleUserNotFound') {
                const signUpParams = new URLSearchParams();
                if (redirectAfterLogin) {
                    const redirectPath = redirectAfterLogin.startsWith('/')
                        ? redirectAfterLogin.substring(1)
                        : redirectAfterLogin;
                    signUpParams.set('redirectAfterRegister', redirectPath);
                }
                const query = signUpParams.toString();
                router.push(`/sign-up${query ? `?${query}` : ''}`);
            } else {
                setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS);
            }
        },
        [router, setLoginErrorType, redirectAfterLogin],
    );

    const handleError = useCallback(() => setLoginErrorType(LoginErrorType.INVALID_CREDENTIALS), [setLoginErrorType]);

    return useGoogleCallback({
        action: RecaptchaAction.LOGIN_WITH_GOOGLE,
        onSuccess: handleSuccess,
        onError: handleError,
    });
}
